import { NavigateFunction } from 'react-router-dom';
import { fetchData, FetchDataError, FetchDataResponse } from './fetch-data';
import { URLParamsBuilder } from './url-params-builder';
import { FORBIDDEN_PATH, LOGIN_PATH, SERVICE_UNAVAILABLE_PATH } from 'user/routes';
import { LoginData } from 'auth/models';

export class FetcherService {
  accessToken: string;
  authenticate: () => Promise<LoginData>;
  navigate: NavigateFunction;
  clearTokens: () => void;
  _initialized = false;

  setup(
    accessToken: string,
    authenticate: () => Promise<LoginData>,
    navigate: NavigateFunction,
    clearTokens: () => void,
  ) {
    this.accessToken = accessToken;
    this.authenticate = authenticate;
    this.navigate = navigate;
    this.clearTokens = clearTokens;
    this._initialized = true;
  }

  async post<T>(
    url: string,
    body?: any,
    isFileRequest?: boolean,
    isPublic?: boolean,
  ): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({
      method: 'POST',
      url,
      body,
      isFileRequest,
      isPublic,
    });
  }

  async get<T>(
    url: string,
    queryParams?: any,
    isFileRequest?: boolean,
    isPublic?: boolean,
  ): Promise<FetchDataResponse<T>> {
    const _url = new URLParamsBuilder(url).addQueryParams(queryParams).build();
    return this.buildFetchRequest({ method: 'GET', url: _url, isFileRequest, isPublic });
  }

  async put<T>(url: string, body?: any): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({
      method: 'PUT',
      url,
      body,
    });
  }
  async patch<T>(url: string, body?: any): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({
      method: 'PATCH',
      url,
      body,
    });
  }

  async del<T>(url: string): Promise<FetchDataResponse<T>> {
    return this.buildFetchRequest({ method: 'DELETE', url });
  }

  private buildDefaultHeaders(
    isPublic: boolean,
    isFileRequest: boolean,
  ): Record<string, string> {
    const defaultHeaders: Record<string, string> = {};

    if (!isPublic) {
      defaultHeaders['Authorization'] = `Bearer ${this.accessToken}`;
    }

    if (!isFileRequest) {
      defaultHeaders['Content-Type'] = 'application/json';
    }

    return defaultHeaders;
  }

  private async handleUnauthorizedError<T>(params: {
    method: string;
    url: string;
    headers?: HeadersInit;
    body?: any;
    isFileRequest?: boolean;
    isPublic?: boolean;
  }, error: FetchDataError): Promise<FetchDataResponse<T> | null> {
    const { method, url, body, headers, isFileRequest = false, isPublic = false } = params;

    if (error.apiError?.message !== 'AuthenticationExpired') {
      if (!isPublic) {
        this.clearTokens();
        this.navigate(LOGIN_PATH);
      }
      return null;
    }

    const tokens = await this.authenticate();
    if (!tokens) {
      this.clearTokens();
      this.navigate(LOGIN_PATH);
      return null;
    }

    this.setup(tokens.accessToken, this.authenticate, this.navigate, this.clearTokens);
    return this.buildFetchRequest({
      method,
      url,
      headers,
      body,
      isFileRequest,
    });
  }

  private handleForbiddenError(error: FetchDataError, url: string): void {
    const isImpersonationError =
      error.apiError?.message === 'action not allowed during impersonation';
    const isImpersonationUrl = url.includes('auth/login/impersonate');

    if (!isImpersonationError && !isImpersonationUrl) {
      this.navigate(FORBIDDEN_PATH);
    }
  }

  private async handleFetchError<T>(
    error: FetchDataError,
    params: {
      method: string;
      url: string;
      headers?: HeadersInit;
      body?: any;
      isFileRequest?: boolean;
      isPublic?: boolean;
      defaultFileName?: string;
    },
  ): Promise<FetchDataResponse<T> | null> {
    switch (error.statusCode) {
      case 401:
        return this.handleUnauthorizedError(params, error);
      case 403:
        this.handleForbiddenError(error, params.url);
        return null;
      case 503:
        this.navigate(SERVICE_UNAVAILABLE_PATH);
        return null;
      default:
        return null;
    }
  }

  private async buildFetchRequest<T>(params: {
    method: string;
    url: string;
    headers?: HeadersInit;
    body?: any;
    isFileRequest?: boolean;
    isPublic?: boolean;
    defaultFileName?: string;
  }): Promise<FetchDataResponse<T>> {
    const {
      method,
      url,
      body,
      headers,
      isFileRequest = false,
      isPublic = false,
      defaultFileName,
    } = params;

    try {
      return await fetchData<T>(
        method,
        url,
        { ...this.buildDefaultHeaders(isPublic, isFileRequest), ...headers },
        body,
        isFileRequest,
        defaultFileName,
      );
    } catch (error) {
      const retryResult = await this.handleFetchError<T>(error as FetchDataError, params);
      if (retryResult) {
        return retryResult;
      }
      throw error;
    }
  }
}
